import { Transform } from 'class-transformer';
import {
  IsBoolean,
  IsEmail,
  IsIn,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';
// Type-only: citizen-report.dto extends a class from this file at runtime, so
// a value import back the other way would be a cycle.
import type { TriageCitizenReport } from './citizen-report.dto';

/**
 * Complaints submitted by the public, and the queue an officer triages them in.
 *
 * The shape of this feature is one decision: **a citizen submission creates a
 * complaint and nothing else.** It does not create an enforcement file, it does
 * not assign an officer, and it does not start any statutory clock. Somebody
 * outside the municipality can put a property in front of it; only the
 * municipality can decide to act. Every other choice here follows from that.
 */

/**
 * The reasons a resident may be told, when the municipality declines.
 *
 * A code rather than the officer's free text: the decline reason an officer
 * writes is internal and can name people ("owner is in hospital"). What goes
 * back to the person who reported is one of these, in their language.
 */
export const DECLINE_PUBLIC_REASONS = [
  'outside_jurisdiction',
  'not_a_bylaw_matter',
  'insufficient_information',
  'already_resolved',
  'other',
] as const;
export type DeclinePublicReason = (typeof DECLINE_PUBLIC_REASONS)[number];

/**
 * Where a complaint has got to.
 *
 * `merged` is the citizen-reporting outcome "this is a matter already on a
 * file": the report is folded into that file rather than opening a second
 * enforcement record for the same property.
 */
export type ComplaintTriageStatus =
  'not_required' | 'untriaged' | 'accepted' | 'declined' | 'merged';

/**
 * Trim before validating, so `MinLength` measures what is actually stored —
 * twenty spaces are not a description. Non-strings pass through untouched for
 * `IsString` to refuse.
 */
const trim = ({ value }: { value: unknown }) => (typeof value === 'string' ? value.trim() : value);

/**
 * A boolean that may arrive as multipart text: "true" is the only truthy
 * string. Reads the raw field (`obj[key]`), not `value` — the APIs' pipe runs
 * with implicit conversion, which has already turned "false" into
 * `Boolean("false")`, i.e. true, by the time `value` is handed over. Anything
 * else passes through for `IsBoolean` to refuse.
 */
export const formBool = ({ obj, key }: { obj: Record<string, unknown>; key: string }) => {
  const raw = obj[key];
  return raw === true || raw === 'true' ? true : raw === false || raw === 'false' ? false : raw;
};

/**
 * Trim, and treat a blank optional field as absent. Without the second half a
 * whitespace-only email would trim to `''`, which `IsOptional` does not skip and
 * `IsEmail` rejects — a 400 for a field the person left empty.
 */
const trimOptional = ({ value }: { value: unknown }) => {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/** The longest a member of the public may make a single report. */
export const CITIZEN_DESCRIPTION_MAX = 4000;

/**
 * What the public form sends.
 *
 * Deliberately small. A citizen is asked for the property, what they have seen,
 * and — only if they want a reply — who they are. Everything the officer intake
 * form collects about parcels, owners and assessment is the municipality's work
 * to do from its own records, not a member of the public's to guess at.
 */
export class SubmitCitizenComplaintDto {
  /**
   * The municipality being reported to.
   *
   * Chosen by the person reporting rather than derived from the address: we
   * cannot geocode reliably enough to route a statutory complaint, and a report
   * that silently lands at the wrong municipality is worse than one that asks.
   */
  @IsUUID()
  localGovernmentId!: string;

  @Transform(trim)
  @IsString()
  @MinLength(1)
  @MaxLength(400)
  subjectAddress!: string;

  /**
   * A floor as well as a ceiling. "Bad house" is not a report anyone can act on,
   * and accepting it produces a queue item that wastes an officer's visit rather
   * than informing it.
   */
  @Transform(trim)
  @IsString()
  @MinLength(20)
  @MaxLength(CITIZEN_DESCRIPTION_MAX)
  description!: string;

  /**
   * Reporting anonymously is explicitly allowed.
   *
   * People report their own neighbours. Forcing a name onto that either
   * suppresses the report or produces a false one, and the complainant's
   * identity is already excluded from everything generated from a file.
   *
   * The string forms are accepted because the citizen reporting form posts
   * multipart, where every field is text.
   */
  @Transform(formBool)
  @IsOptional()
  @IsBoolean()
  anonymous?: boolean;

  @Transform(trimOptional)
  @IsOptional()
  @IsString()
  @MaxLength(200)
  complainantName?: string;

  /** Only used to acknowledge the report. Never published, never served. */
  @Transform(trimOptional)
  @IsOptional()
  @IsEmail()
  @MaxLength(320)
  complainantEmail?: string;

  @Transform(trimOptional)
  @IsOptional()
  @IsString()
  @MaxLength(32)
  complainantPhone?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  locale?: 'en' | 'fr';
}

/**
 * What the submitter gets back.
 *
 * A reference and nothing else. The form must not become a way to learn whether
 * a property already has a file, who owns it, or what the municipality has done
 * about it — a report is a one-way channel by design.
 */
export interface CitizenComplaintReceipt {
  reference: string;
  /** Whether an acknowledgement was sent, so the page can say so truthfully. */
  acknowledgementSent: boolean;
}

/** One report waiting on an officer. */
export interface TriageQueueItem {
  id: string;
  reference: string;
  subjectAddress: string;
  description: string;
  createdAt: string;
  anonymous: boolean;
  complainantName: string | null;
  complainantEmail: string | null;
  complainantPhone: string | null;
  locale: 'en' | 'fr' | null;
  status: ComplaintTriageStatus;
  /**
   * Open files already at this address, if any.
   *
   * Shown rather than acted on: a second report about a property already under
   * enforcement is corroboration, and the officer decides whether it is a
   * duplicate. Dropping it on the way in would make the channel quietly lossy.
   */
  openFiles: Array<{ id: string; reference: string; stage: string }>;
  /** Set once triaged. */
  triagedByName: string | null;
  triagedAt: string | null;
  declinedReason: string | null;
  /** The file opened from this report, when it was accepted. */
  fileId: string | null;
  fileReference: string | null;
  /** The file this report was folded into, when it was merged as a duplicate. */
  mergedIntoFileId: string | null;
  mergedIntoFileReference: string | null;
  /** What the reporter was told when it was declined. */
  declinedPublicReason: DeclinePublicReason | null;
  /**
   * Category, photos, pin and likely duplicates — present only for reports
   * made through citizen reporting, and only to a caller holding `report:read`
   * at the municipality.
   */
  citizenReport: TriageCitizenReport | null;
}

export class DeclineComplaintDto {
  /**
   * Required. A municipality that declines to act should be able to say why,
   * months later, to the person who reported it.
   */
  @IsString()
  @MinLength(1)
  @MaxLength(2000)
  reason!: string;

  /**
   * What the person who reported it is told, for a citizen report. The
   * free-text {@link reason} stays internal: it is written for colleagues and
   * can name people.
   */
  @IsOptional()
  @IsIn(DECLINE_PUBLIC_REASONS)
  publicReason?: DeclinePublicReason;
}
